import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { useState } from 'react';
import { AsrProxyService } from '../../api/AsrProxyService';
import { createFetchAsrModelsQueryFn } from '../../api/fetchAsrModels';
import type { AsrModelsResponse } from '../../api/types';
import { HealthDot } from '../HealthDot';

export const ModelsTab = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');

  const modelsQuery = useQuery({
    queryKey: asrProxyKeys.models(),
    queryFn: createFetchAsrModelsQueryFn(call),
  });
  const models = modelsQuery.data?.models ?? [];
  const defaultModel = modelsQuery.data?.default_model;

  const healthCheckMutation = useMutation({
    mutationFn: () => call(() => AsrProxyService.runHealthCheck(), { dontRedirectOnError: true, onError: setMessage }),
    onMutate: () => {
      setMessage('');
      queryClient.setQueryData<AsrModelsResponse>(asrProxyKeys.models(), (current) =>
        current ? { ...current, models: current.models.map((model) => ({ ...model, health: undefined })) } : current,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: asrProxyKeys.models() });
      setMessage('Health check complete');
    },
  });

  const checking = healthCheckMutation.isPending;
  const displayedMessage = message || getApiErrorMessage(modelsQuery.error);
  const isLoading = modelsQuery.isFetching || checking;

  return (
    <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)]">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-foreground">Models and providers</h2>
          <div className="mt-1 text-sm text-foreground-muted">
            Default model:{' '}
            <code className="rounded bg-page px-1.5 py-0.5 text-xs text-foreground">{defaultModel ?? '—'}</code>
          </div>
        </div>
        <div className="flex gap-2">
          <OutlinedButton className="!w-auto" disabled={checking} onClick={() => healthCheckMutation.mutate()}>
            {checking ? 'Checking…' : 'Run health check'}
          </OutlinedButton>
          <OutlinedButton className="!w-auto" disabled>
            Reload registry
          </OutlinedButton>
        </div>
      </div>
      <p className="mb-4 text-xs text-foreground-muted">
        Registry reload is unavailable while the fixed AssemblyAI Universal-2 registry is in use.
      </p>
      {displayedMessage ? <p className="mb-3 text-sm text-foreground-muted">{displayedMessage}</p> : null}
      <LinearProgressLoader active={isLoading} />
      <TableWrapper>
        <Table>
          <Thead $white>
            <Tr>
              <Th>Public model</Th>
              <Th>Provider</Th>
              <Th>Provider model</Th>
              <Th className="text-center">API key</Th>
              <Th className="text-center">Diarization</Th>
              <Th className="text-center">Multichannel</Th>
              <Th className="text-center">Health</Th>
            </Tr>
          </Thead>
          <Tbody>
            {models.map((model) => (
              <Tr key={model.model_name}>
                <Td>
                  <code className="rounded bg-page px-1.5 py-0.5 text-xs text-foreground">{model.model_name}</code>
                </Td>
                <Td>{model.provider}</Td>
                <Td>{model.provider_model}</Td>
                <Td className="text-center">
                  {model.has_api_key ? '✓' : <span className="text-[var(--color-danger)]">✗</span>}
                </Td>
                <Td className="text-center">{model.info.supports_diarization ? '✓' : '—'}</Td>
                <Td className="text-center">{model.info.supports_multichannel ? '✓' : '—'}</Td>
                <Td className="text-center">
                  <HealthDot health={model.health} />
                </Td>
              </Tr>
            ))}
            {models.length === 0 ? (
              <Tr>
                <Td className="text-foreground-muted" colSpan={7}>
                  No models loaded.
                </Td>
              </Tr>
            ) : null}
          </Tbody>
        </Table>
      </TableWrapper>
    </section>
  );
};
